import { SESSION_ACTIONS, SessionAction } from '../actions/session';
import { getObjVal, setAuthTokenFromCookie } from '../constants';
import {
  Dispatch,
  GlobalStatusType,
  PartnerTypeEnum,
  SensorEntry,
  SessionStorageKey,
  StatusType,
  Store,
  SwitchableCustomerType,
  TeamMemberType,
  UserRole,
  UserRoleEnum,
  UserType,
} from '../types';

type TSecurityScoreSizeStat = {
  num_employees_lt: number | null;
  num_employees_gte: number;
  name: string;
  value: number;
};

type TSecurityScoreStats = {
  industry: number | null;
  avg: number;
  size: TSecurityScoreSizeStat | null;
};

type TOverridable = string; // i.e. "N/A"

type TSecurityScores = {
  // overridable components
  ad_nxlog_parser_exists: boolean | TOverridable;
  ad_poller_logs_exist: boolean | TOverridable;
  customer_in_geo_approved_or_restricted_list: boolean | TOverridable;
  fw_log_parser_exists: boolean | TOverridable;
  has_recent_snmp_observations: number | TOverridable;
  server_av_found: boolean | TOverridable;
  num_internal_nmap_scanned_in_past_week: number | TOverridable;
  num_critical_vulnerabilities: number | TOverridable;
  num_escalations_configured: number | TOverridable;
  num_scan_types_configured: number | TOverridable;
  num_monthly_incidents: number | TOverridable;
  desktop_av_found: boolean | TOverridable;
  num_high_vulnerabilities: number | TOverridable;

  // if false, this overrides num_cloud_apps_configured & num_cloud_apps_detected
  obs_exist_for_configured_cloud_apps: boolean | TOverridable;

  // nullable components
  num_cloud_apps_configured: number | null;
  num_cloud_apps_detected: number | null;

  // info
  timestamp: string;
  industry: string;
  security_score: number;

  incidents_score: number;
  configuration_score: number;
  exscan_score: number;
  ticketing_score: number;
  monitoring_score: number;
};

export type TSecurityReadiness = {
  stats: TSecurityScoreStats;
  scores: TSecurityScores;
};

export type State = {
  /**
   * abortControllers: a list of AbortController to be aborted on customer switch
   */
  readonly abortControllers: ReadonlyArray<AbortController>;
  readonly customers: Array<SwitchableCustomerType>;
  readonly globalStatus: Array<GlobalStatusType>;
  readonly isSwitchingCustomer: boolean;
  readonly user: UserType | null;
  readonly logoutStatus: StatusType | null;
  readonly isBrowserAlertClosed: boolean;
  readonly useFrontendAuth0: boolean;
  readonly isLegacyLoginPage: boolean;
  readonly loginEmail: string | null;
  readonly loginError: string | null;
};

export const INITIAL_SESSION_STATE: State = {
  abortControllers: [],
  customers: [],
  globalStatus: [],
  isSwitchingCustomer: false,
  user: null,
  logoutStatus: null,
  isBrowserAlertClosed: false,
  useFrontendAuth0: !!window.sessionStorage.getItem(
    SessionStorageKey.USE_AUTH0_FRONTEND,
  ),
  // isLegacyLoginPage for the legacy login page
  // when true is using the Login page to get the user login email
  // when false is to using the auth0 login and auth0 email.
  isLegacyLoginPage:
    typeof process !== 'undefined' && process?.env?.NODE_ENV === 'development',
  loginEmail: null,
  loginError: null,
};

const sessionReducer = (
  state: State = INITIAL_SESSION_STATE,
  action: SessionAction,
): State => {
  switch (action.type) {
    case 'login':
      if (action.primary_token) {
        sessionStorage.setItem(
          SessionStorageKey.PRIMARY_TOKEN,
          action.primary_token,
        );
      }
      if (action.secondary_token) {
        sessionStorage.setItem(
          SessionStorageKey.SECONDARY_TOKEN,
          action.secondary_token,
        );
      }

      sessionStorage.removeItem(SessionStorageKey.AUTH_MESSAGE);
      sessionStorage.removeItem(SessionStorageKey.LOGIN_ERROR);

      try {
        //@ts-ignore: TODO - Sentry + Typescript
        window.Raven.setUserContext({
          email: getObjVal(action, 'user.email', 'Unknown'),
        });
      } catch (e) {}

      return {
        ...state,
        user: action.user,
      };

    case 'logout':
      sessionStorage.removeItem(SessionStorageKey.PRIMARY_TOKEN);
      sessionStorage.removeItem(SessionStorageKey.SECONDARY_TOKEN);
      sessionStorage.removeItem(SessionStorageKey.USE_AUTH0);
      sessionStorage.setItem(
        SessionStorageKey.AUTH_MESSAGE,
        JSON.stringify(action.status),
      );
      window.sessionStorage.setItem(SessionStorageKey.LOGGED_OUT, 'true');
      window.sessionStorage.removeItem(SessionStorageKey.USE_AUTH0_FRONTEND);
      window.sessionStorage.removeItem(SessionStorageKey.LOGIN_ERROR);
      window.sessionStorage.removeItem(SessionStorageKey.AUTH0_EMAIL);

      try {
        //@ts-ignore: TODO - Sentry + Typescript
        window.Raven.setUserContext();
      } catch (e) {}

      return {
        ...INITIAL_SESSION_STATE,
        logoutStatus: action.status,
        useFrontendAuth0: false,
        loginEmail: '',
      };

    case 'set_customer_list':
      action.customers.sort((a, b) => a.name.localeCompare(b.name));
      return {
        ...state,
        customers: action.customers,
      };

    case 'switch_customer': {
      if (action.user) {
        setAuthTokenFromCookie();
      }
      return {
        ...state,
        user: action.user,
        isSwitchingCustomer: !action.user,
      };
    }

    case 'set_onboarding_status':
      if (state.user?.customer) {
        return {
          ...state,
          user: {
            ...state.user,
            customer: {
              ...state.user.customer,
              ...action.status,
            },
          },
        };
      }
      return {
        ...state,
      };

    case 'set_global_status':
      return {
        ...state,
        globalStatus: [...state.globalStatus, action.status],
      };

    case 'close_browser_alert':
      return {
        ...state,
        isBrowserAlertClosed: action.isBrowserAlertClosed,
      };

    case 'clear_global_status_serial':
      const serialNumber = action.serialNumber;

      if (
        state.globalStatus.length &&
        state.globalStatus.find(
          (status) => status.serialNumber === serialNumber,
        )
      ) {
        const updatedStatus = state.globalStatus.filter(
          (status) => status.serialNumber !== serialNumber,
        );
        return { ...state, globalStatus: updatedStatus };
      }

      return state;

    case 'register_abort_controller':
      return {
        ...state,
        abortControllers: [...state.abortControllers, action.abortController],
      };

    case 'remove_abort_controller':
      return {
        ...state,
        abortControllers: state.abortControllers.filter(
          (controller) => controller !== action.abortController,
        ),
      };

    case 'set_auth0_frontend':
      return {
        ...state,
        useFrontendAuth0: action.status,
      };

    case 'set_login_email':
      return {
        ...state,
        loginEmail: action.email,
        useFrontendAuth0: true,
      };

    case 'set_login_error':
      window.sessionStorage.setItem(
        SessionStorageKey.LOGIN_ERROR,
        action.error,
      );
      window.sessionStorage.removeItem(SessionStorageKey.USE_AUTH0_FRONTEND);
      return {
        ...state,
        loginError: action.error,
        useFrontendAuth0: false,
        loginEmail: '',
      };

    default:
      return state;
  }
};

export default sessionReducer;

/**
 * Create and return an instance of AbortController and put it in the redux store
 * This should be used to prevent Chrome from blocking new requests being sent
 *   while waiting on previous requests to same URL (e.g. after customer switch)
 */
export const registerNewAbortController = (
  dispatch: Dispatch,
): AbortController => {
  const abortController = new AbortController();
  dispatch(SESSION_ACTIONS.REGISTER_ABORT_CONTROLLER(abortController));
  return abortController;
};

/**
 * Call abort on each of the AbortControllers in the store
 * This can't be part of the sessionReducer because it will be executed by
 *   the root reducer (see index.tsx)
 */
export const abortAllRequests = (store: Store) => {
  for (const controller of sessionSelector.getAbortControllers(store)) {
    controller.abort();
  }
};

// ------------
// Selectors
// -------------
export const sessionSelector = {
  getAbortControllers: (store: Store): ReadonlyArray<AbortController> =>
    store.session.abortControllers,
  useFrontendAuth0: (store: Store): boolean => store.session.useFrontendAuth0,
  isLegacyLoginPage: (store: Store): boolean => store.session.isLegacyLoginPage,
  loginEmail: (store: Store): string | null => store.session.loginEmail,
  loginError: (store: Store): string | null => store.session.loginError,
  isAwnUser: (store: Store): boolean =>
    getObjVal(store, 'session.user.email', '').endsWith('@arcticwolf.com'),
  getCustomerPartnerType: (store: Store): PartnerTypeEnum | undefined =>
    store.session.user?.customer?.partner_type,
  getUsername: (store: Store): string | null =>
    getObjVal(store, 'session.user.name', null),
  getUserEmail: (store: Store): string | null =>
    getObjVal(store, 'session.user.email', null),
  getCustomerName: (store: Store): string | null =>
    getObjVal(store, 'session.user.customer.name', null),
  getCustomerPod: (store: Store): string | undefined =>
    store.session.user?.customer?.pod,
  getCustomerID: (store: Store): string | null =>
    getObjVal(store, 'session.user.customer.id', null),
  getMSPID: (store: Store): string | null =>
    getObjVal(store, 'session.user.msp_id', null),
  getCST: (store: Store): Array<TeamMemberType> =>
    getObjVal(store, 'session.user.customer.cst', []),
  getAllUsersCanComment: (store: Store) =>
    getObjVal(store, 'session.user.customer.all_users_can_comment', false),
  getGlobalStatus: (store: Store): Array<GlobalStatusType> =>
    store.session.globalStatus,
  getRetentionPeriod: (store: Store): number | null =>
    getObjVal(store, 'session.user.customer.retention', null),
  getRoles: (store: Store): Array<UserRole> =>
    getObjVal(store, 'session.user.roles', []),
  hasRole: (store: Store, role: UserRole) =>
    getObjVal(store, 'session.user.roles', []).includes(role),
  isMSP: (store: Store) =>
    store.session.user?.roles.includes(UserRoleEnum.VIEW_MSPSWITCH) ?? false,
  isFeatureFlagEnabled:
    (featureFlag: string) =>
    (store: Store): boolean =>
      getObjVal(store, `session.user.feature_flags.${featureFlag}`, false),
  getSensors: (store: Store): Array<SensorEntry> =>
    getObjVal(store, 'session.user.customer.sensors', []),
  getLogoutStatus: (store: Store): StatusType | null =>
    store.session.logoutStatus,
  getSwitchableCustomers: (store: Store): Array<SwitchableCustomerType> =>
    store.session.customers,
  isSwitchingCustomer: (store: Store): boolean =>
    store.session.isSwitchingCustomer,
  getSecurityReadiness: (store: Store) =>
    getObjVal(store, 'session.user.customer.security_readiness', null),
  getSecurityReadinessScore: (store: Store): number | null =>
    getObjVal(
      store,
      'session.user.customer.security_readiness.scores.security_score',
      null,
    ),
  getFeatureFlagList:
    (featureFlag: string) =>
    (store: Store): Array<string> =>
      getObjVal(store, `session.user.feature_flags.${featureFlag}`, []),
  isBrowserAlertClosed: (store: Store): boolean =>
    store.session.isBrowserAlertClosed,
  isCustomerOnboarding: (store: Store): boolean =>
    store.session.user?.customer?.is_onboarding ?? false,
  isHandoffComplete: (store: Store): boolean =>
    store.session.user?.customer?.onboarding_status?.handoff_completed ?? false,
  canEditOnboarding: (store: Store): boolean =>
    store.session.user?.customer?.onboarding_status?.can_edit_sections ?? true,
  hasCompletedTKO: (store: Store): boolean =>
    store.session.user?.customer?.onboarding_status?.tko_completed ?? false,
  isPartiallyComplete: (store: Store): boolean =>
    store.session.user?.customer?.onboarding_status?.partially_completed ??
    false,
  isReadyForHandoverOrAcceptance: (store: Store): boolean =>
    store.session.user?.customer?.onboarding_status
      ?.ready_for_handover_or_acceptance ?? false,
  getLogSearchIncidentMessage: (store: Store): string =>
    (store.session.user?.feature_flags
      ?.log_search_incident_message as string) ?? '',
  getRiskID: (store: Store): string | undefined =>
    getObjVal(store, 'session.user.customer.riskID', undefined),
};

export const featureFlagSelector = {
  onboarding: {
    scannerPlatformization: sessionSelector.isFeatureFlagEnabled(
      'scanner_platformization',
    ),
    vanillaSensor: sessionSelector.isFeatureFlagEnabled('vanilla_sensor'),
    vanillaScanner: sessionSelector.isFeatureFlagEnabled('vanilla_scanner'),
    virtualSensors: sessionSelector.isFeatureFlagEnabled(
      'onboarding_virtual_sensors',
    ),
    additionalEndpointSymantecBroadcom: sessionSelector.isFeatureFlagEnabled(
      'additional_endpoint_symantec_broadcom',
    ),
    domainOwnershipConfirmed: sessionSelector.isFeatureFlagEnabled(
      'dns_ownership_confirmation',
    ),
  },
  credentials: {
    listCloudApps: sessionSelector.isFeatureFlagEnabled('list_cloud_apps'),
  },
  logSearchV2: sessionSelector.isFeatureFlagEnabled('log_downloads_enabled'),
  platformColumn: sessionSelector.isFeatureFlagEnabled('platform_column'),
};
